<script setup lang="ts">
/**
 * Le détail d'une alarme (écran 7 du dossier), sous la fiche de son
 * équipement, en lecture.
 *
 * Ce que l'utilisateur doit vivre : il ouvre une alarme et voit la courbe
 * autour du dépassement, le seuil, depuis quand, qui a été prévenu et par quel
 * canal, qui a acquitté.
 *
 * ⚠️ OUVRIR CETTE PAGE A UN EFFET : la lecture du détail (route 12) clôt une
 * alarme revenue à la normale et non vue, en nommant le lecteur (D71, geste
 * 39 `episode_vu`). C'est la SEULE page qui la lance, une fois par ouverture
 * voulue (entrée sur la page, ou une autre alarme ouverte par un lien) :
 * jamais au survol, jamais en préchargement, et pas de relecture d'elle-même
 * quand la session change, la page se vide (garde `gardes.statiques.spec.ts`).
 *
 * Lot F6 : « Acquitter » ne relit JAMAIS la route 12. Le détail prend
 * l'acquittement que le serveur a rendu (route 7), puis la surveillance est
 * relue par la liste (`ficheStore.relire`), dont l'alarme ouverte, si c'est
 * celle-ci, donne l'état enregistré (après un délai dépassé surtout).
 */
// ================================
// IMPORTS
// ================================
import { computed, watch } from "vue"
import { useI18n } from "vue-i18n"
import { useAuthStore } from "@core/stores/auth.store"
import { useDetailAlarmeStore } from "@modules/alarmes-v2/stores/detailAlarme.store"
import { useFicheStore } from "@modules/alarmes-v2/stores/fiche.store"
import { useTypesStore } from "@modules/alarmes-v2/stores/types.store"
import { CODE_INDISPONIBLE } from "@modules/alarmes-v2/services/appel"
import { SEGMENT_SURVEILLANCE } from "@modules/alarmes-v2/utils/menu"
import { cleDuRefus } from "@modules/alarmes-v2/utils/refus"
import type { AlarmesV2Acquittement } from "@modules/alarmes-v2/types/alarmes-v2"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n()

// ================================
// PROPS & EMITS
// ================================
const props = defineProps<{
    objetType: "source" | "building"
    idObjet: string
    idInstance: string
    idAlarme: string
}>()

const authStore = useAuthStore()
const detailStore = useDetailAlarmeStore()
const ficheStore = useFicheStore()
const typesStore = useTypesStore()

/**
 * Le chemin de la fiche de la surveillance.
 */
const getCheminFiche = computed(
    (): string =>
        `/${props.objetType}/${encodeURIComponent(props.idObjet)}/${SEGMENT_SURVEILLANCE}/${encodeURIComponent(props.idInstance)}`
)

/**
 * La phrase d'un échec, à la place du détail.
 */
const getPhraseErreur = computed((): string => {
    const erreur = detailStore.erreur
    if (!erreur || erreur.code === CODE_INDISPONIBLE)
        return t("alarmesV2.detail.indisponible")
    return t(cleDuRefus(erreur))
})

/**
 * Vrai quand la surveillance de l'adresse n'est pas sur l'équipement de
 * l'adresse (lien modifié ou collé) : le détail n'est pas montré sous cet
 * équipement, la phrase neutre le dit.
 */
const getHorsEquipement = computed(
    (): boolean =>
        ficheStore.statut === "introuvable" &&
        ficheStore.cleLue.endsWith(
            `|${props.objetType}|${props.idObjet}|${props.idInstance}`
        )
)

/**
 * La surveillance de l'en-tête, si elle est lue et que c'est bien la sienne.
 */
const getSurveillance = computed(() =>
    ficheStore.surveillance?._id === props.idInstance
        ? ficheStore.surveillance
        : null
)

/**
 * Le nom du type d'alarme, ou `null`.
 */
const getNomType = computed((): string | null => {
    const a = detailStore.detail?.occurrence
    const s = getSurveillance.value
    const code = a?.type_code ?? s?.type_code
    const version = a?.type_version ?? s?.type_version
    return code && typeof version === "number"
        ? typesStore.nomDuType(code, version)
        : null
})

/**
 * Pose sur le détail l'acquittement que le serveur a rendu.
 */
function appliquer(reponse: AlarmesV2Acquittement): void {
    detailStore.appliquerAcquittement(props.idAlarme, reponse)
}

/**
 * Relit après un acquittement, par la LISTE (jamais la route 12) : la
 * surveillance et son alarme ouverte. Si l'alarme ouverte est celle du
 * détail, son état enregistré est posé sur le détail.
 */
async function relire(): Promise<boolean> {
    const ok = await ficheStore.relire({
        objetType: props.objetType,
        idObjet: props.idObjet,
        idInstance: props.idInstance,
    })
    const a = ficheStore.alarmeOuverte
    if (ok && a?._id === props.idAlarme)
        appliquer({
            etat: a.etat,
            prise_en_compte: a.prise_en_compte,
            personnes: ficheStore.personnes,
        })
    return ok
}

/**
 * Ouvre le détail : UNE lecture de la route 12, sur l'entrée de la personne.
 */
function ouvrir(): void {
    if (!authStore.hasOfferEnergyManager) return
    void detailStore.ouvrirLeDetail(props.idAlarme, props.idInstance)
    void ficheStore.charger({
        objetType: props.objetType,
        idObjet: props.idObjet,
        idInstance: props.idInstance,
    })
    void typesStore.charger()
}

// Une ouverture par alarme ouverte par la personne : l'entrée sur la page, ou
// un lien vers une autre alarme.
watch(() => props.idAlarme, ouvrir, { immediate: true })

// Une autre session ne relit pas le détail d'elle-même : elle le vide.
watch(
    () => [authStore.companyId, authStore.userId],
    () => {
        detailStore.vider()
        ficheStore.reinitialiser()
    }
)

/**
 * Relit le détail après un échec : un geste de la personne.
 */
function onRelire(): void {
    ouvrir()
}
</script>

<template>
    <!-- ================================ -->
    <!-- COMPOSANT PRINCIPAL -->
    <!-- ================================ -->
    <CadreModule>
        <div class="flex flex-col gap-4 p-4 md:p-6">
            <RouterLink
                :to="getCheminFiche"
                class="text-sm text-primary-700 underline"
                data-test="vers-fiche"
                >{{ t("alarmesV2.detail.versLaFiche") }}</RouterLink
            >
            <section
                class="rounded-lg border border-surface-200 bg-surface-0 p-4"
            >
                <!-- La surveillance doit être retrouvée sur l'équipement avant que le détail se montre sous lui. -->
                <p
                    v-if="
                        detailStore.statut === 'chargement' ||
                        ficheStore.statut === 'chargement'
                    "
                    class="text-sm text-surface-600"
                >
                    {{ t("alarmesV2.detail.chargement") }}
                </p>
                <p
                    v-else-if="getHorsEquipement"
                    class="text-sm text-surface-800"
                    data-test="erreur"
                >
                    {{ t("alarmesV2.refus.neutre") }}
                </p>
                <div
                    v-else-if="detailStore.statut === 'erreur'"
                    class="flex flex-wrap items-center gap-3"
                    data-test="erreur"
                >
                    <p class="text-sm text-surface-800">
                        {{ getPhraseErreur }}
                    </p>
                    <button
                        type="button"
                        class="text-sm font-medium text-primary-700 underline"
                        @click="onRelire"
                    >
                        {{ t("alarmesV2.refus.relire") }}
                    </button>
                </div>
                <DetailAlarme
                    v-else-if="detailStore.detail"
                    :detail="detailStore.detail"
                    :surveillance="getSurveillance"
                    :nom-type="getNomType"
                    :relire="relire"
                    :appliquer="appliquer"
                />
            </section>
        </div>
    </CadreModule>
</template>
